import type { Cart, CartLine } from '@repo/shopify';

/**
 * Optimistic line mutations layered over the server-confirmed cart with React's
 * `useOptimistic` (see CartProvider). Each change is applied to whatever the
 * latest confirmed cart is, so it must be idempotent: re-applying "set line X
 * to 3" or "remove line Y" on a cart that already reflects it is a no-op.
 *
 * Totals are adjusted by the delta of the affected lines only — order-level
 * discounts, shipping and tax stay whatever the server last said — so the
 * drawer shows a close approximation until the confirmed cart lands.
 */
export type OptimisticLineChange =
	| { type: 'quantity'; lineId: string; quantity: number }
	| { type: 'remove'; lineIds: readonly string[] };

function toAmount(value: number) {
	// Shopify money amounts are decimal strings; fix to cents so scaled values
	// don't carry float noise (35.999999…) into formatMoney.
	return (Math.round(value * 100) / 100).toFixed(2);
}

function parseAmount(value: string | undefined | null) {
	const parsed = Number.parseFloat(value ?? '');
	return Number.isFinite(parsed) ? parsed : 0;
}

function scaleLine(line: CartLine, quantity: number): CartLine {
	const previousTotal = parseAmount(line.cost.totalAmount.amount);
	// Scale the confirmed line total proportionally so a line-level discount
	// (bundle ladder, Subscribe & Save) keeps its ratio instead of snapping to
	// the undiscounted unit price.
	const total =
		line.quantity > 0
			? (previousTotal / line.quantity) * quantity
			: parseAmount(line.cost.amountPerQuantity.amount) * quantity;

	return {
		...line,
		quantity,
		cost: {
			...line.cost,
			totalAmount: { ...line.cost.totalAmount, amount: toAmount(total) },
		},
	};
}

function shiftCartTotals(cart: Cart, quantityDelta: number, amountDelta: number): Cart {
	if (quantityDelta === 0 && amountDelta === 0) return cart;

	return {
		...cart,
		totalQuantity: Math.max(0, cart.totalQuantity + quantityDelta),
		cost: {
			...cart.cost,
			subtotalAmount: {
				...cart.cost.subtotalAmount,
				amount: toAmount(Math.max(0, parseAmount(cart.cost.subtotalAmount.amount) + amountDelta)),
			},
			totalAmount: {
				...cart.cost.totalAmount,
				amount: toAmount(Math.max(0, parseAmount(cart.cost.totalAmount.amount) + amountDelta)),
			},
		},
	};
}

export function applyOptimisticLineChange(
	cart: Cart | null,
	change: OptimisticLineChange,
): Cart | null {
	if (!cart) return cart;

	switch (change.type) {
		case 'quantity': {
			const edge = cart.lines.edges.find((candidate) => candidate.node.id === change.lineId);
			if (!edge || edge.node.quantity === change.quantity) return cart;

			const nextLine = scaleLine(edge.node, change.quantity);
			const amountDelta =
				parseAmount(nextLine.cost.totalAmount.amount) -
				parseAmount(edge.node.cost.totalAmount.amount);

			return shiftCartTotals(
				{
					...cart,
					lines: {
						...cart.lines,
						edges: cart.lines.edges.map((candidate) =>
							candidate === edge ? { ...candidate, node: nextLine } : candidate,
						),
					},
				},
				change.quantity - edge.node.quantity,
				amountDelta,
			);
		}
		case 'remove': {
			const removeIds = new Set(change.lineIds);
			const removed = cart.lines.edges.filter((edge) => removeIds.has(edge.node.id));
			if (removed.length === 0) return cart;

			let quantityDelta = 0;
			let amountDelta = 0;
			for (const edge of removed) {
				quantityDelta -= edge.node.quantity;
				amountDelta -= parseAmount(edge.node.cost.totalAmount.amount);
			}

			return shiftCartTotals(
				{
					...cart,
					lines: {
						...cart.lines,
						edges: cart.lines.edges.filter((edge) => !removeIds.has(edge.node.id)),
					},
				},
				quantityDelta,
				amountDelta,
			);
		}
		default:
			return cart;
	}
}
